import { useState, useRef, useCallback, useEffect } from "react";

/**
 * Custom hook for pull-to-refresh functionality
 * Handles the pull gesture and triggers refresh when threshold is met
 */
const usePullToRefresh = ({
  onRefresh,
  threshold = 80,
  enabled = true,
  containerRef,
}) => {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const touchStartRef = useRef(null);
  const initialScrollTopRef = useRef(0);

  // Handle touch start
  const handleTouchStart = useCallback(
    (event) => {
      if (!enabled || !containerRef?.current) return;

      const container = containerRef.current;
      const scrollTop = container.scrollTop;

      // Only enable pull-to-refresh when at the top of the list
      if (scrollTop > 0) return;

      const touch = event.touches[0];
      touchStartRef.current = {
        clientY: touch.clientY,
        timestamp: Date.now(),
      };

      initialScrollTopRef.current = scrollTop;
    },
    [enabled, containerRef]
  );

  // Handle touch move
  const handleTouchMove = useCallback(
    (event) => {
      if (!enabled || !touchStartRef.current || !containerRef?.current) return;

      const container = containerRef.current;
      const scrollTop = container.scrollTop;

      // Only continue if we're still at the top
      if (scrollTop > 0 && !isPulling) return;

      const touch = event.touches[0];
      const deltaY = touch.clientY - touchStartRef.current.clientY;

      // Only handle downward pulls
      if (deltaY > 0) {
        event.preventDefault();

        // Calculate pull distance with diminishing returns
        const rawDistance = deltaY;
        const dampedDistance = Math.min(
          rawDistance * 0.5, // Damping factor
          threshold * 1.5 // Maximum pull distance
        );

        setIsPulling(true);
        setPullDistance(dampedDistance);
      }
    },
    [enabled, containerRef, isPulling, threshold]
  );

  // Handle touch end
  const handleTouchEnd = useCallback(async () => {
    if (!enabled || !isPulling) return;

    if (pullDistance >= threshold && onRefresh && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } catch (error) {
        console.error("Pull-to-refresh failed:", error);
      } finally {
        setIsRefreshing(false);
      }
    }

    // Reset state
    touchStartRef.current = null;
    setIsPulling(false);
    setPullDistance(0);
  }, [enabled, isPulling, pullDistance, threshold, onRefresh, isRefreshing]);

  // Handle touch cancel
  const handleTouchCancel = useCallback(() => {
    touchStartRef.current = null;
    setIsPulling(false);
    setPullDistance(0);
  }, []);

  // Get pull indicator style
  const getPullIndicatorStyle = useCallback(() => {
    if (!isPulling && !isRefreshing) {
      return {
        transform: "translateY(-100%)",
        opacity: 0,
      };
    }

    const progress = Math.min(pullDistance / threshold, 1);
    const translateY = isPulling ? pullDistance - threshold : 0;

    return {
      transform: `translateY(${translateY}px)`,
      opacity: isPulling ? progress : 1,
      transition: isPulling ? "none" : "all 0.3s ease-out",
    };
  }, [isPulling, isRefreshing, pullDistance, threshold]);

  // Get container transform style
  const getContainerStyle = useCallback(() => {
    if (!isPulling && !isRefreshing) {
      return {
        transform: "translateY(0)",
        transition: "transform 0.3s ease-out",
      };
    }

    const translateY = isPulling ? pullDistance : isRefreshing ? threshold : 0;

    return {
      transform: `translateY(${translateY}px)`,
      transition: isPulling ? "none" : "transform 0.3s ease-out",
    };
  }, [isPulling, isRefreshing, pullDistance, threshold]);

  // Auto-hide refresh indicator after completion
  useEffect(() => {
    if (isRefreshing) {
      const timer = setTimeout(() => {
        setIsRefreshing(false);
      }, 1000); // Show success state for 1 second

      return () => clearTimeout(timer);
    }
  }, [isRefreshing]);

  return {
    // Touch event handlers
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel,

    // State
    isPulling,
    pullDistance,
    isRefreshing,

    // Style helpers
    getPullIndicatorStyle,
    getContainerStyle,

    // Utility
    shouldShowIndicator: isPulling || isRefreshing,
    pullProgress: Math.min(pullDistance / threshold, 1),
  };
};

export default usePullToRefresh;
